import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../util/api";
import { STATES, MEMBERSHIP_LABELS } from "../../util/constant";

const Section = styled.div`
  margin-bottom: 24px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
`;

const SectionHeader = styled.div`
  background: #195174;
  color: white;
  padding: 12px 16px;
  font-size: 1.2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    padding: 12px 16px;
    vertical-align: top;
    text-align: left;
  }
  th h2, th h4 { margin: 0; }
`;

const Select = styled.select`
  width: 100%; max-width: 300px;
  padding: 8px;
  border: 1px solid #195174;
  border-radius: 4px;
`;

const Input = styled.input`
  width: 100%; max-width: 300px;
  padding: 8px;
  border: 1px solid #195174;
  border-radius: 4px;
`;

export default function OrgInfoStep({ data, onChange, step }) {
  const isEducator = data.membership === "ed";
  // educator sub-steps: step 4→1, 5→2, 6→3, 7→4
  const sub = step - 3;

  const [zipcodes, setZipcodes]         = useState([]);
  const [schools,   setSchools]         = useState([]);
  const [schoolLevels, setSchoolLevels] = useState([]);

  // Educator: fetch ZIPs on sub=2
  useEffect(() => {
    if (isEducator && sub === 2 && data.org_state) {
      API.getZipByState({ state: data.org_state })
        .then(resp => {
          const codes = Array.isArray(resp.zip)
            ? resp.zip.map(z => z.zip)
            : [];
          setZipcodes(codes);
        })
        .catch(() => setZipcodes([]));
    }
  }, [isEducator, sub, data.org_state]);

  // Educator: fetch schools on sub=3
  useEffect(() => {
    if (isEducator && sub === 3 && data.org_zip) {
      API.getSchoolByZip({ zip: data.org_zip })
        .then(resp => {
          const list = Array.isArray(resp.schools)
            ? resp.schools.map(s => ({
                org_id:   s.org_id,
                org_name: s.org_name
              }))
            : [];
          setSchools(list);
        })
        .catch(() => setSchools([]));
    }
  }, [isEducator, sub, data.org_zip]);

  // Non-educator: fetch school levels once
  useEffect(() => {
    if (!isEducator) {
      API.getSchoolLevel()
        .then(resp => {
          if (Array.isArray(resp.school_levels)) {
            setSchoolLevels(resp.school_levels);
          }
        })
        .catch(() => setSchoolLevels([]));
    }
  }, [isEducator]);

  return (
    <Section>
      <SectionHeader>
        {isEducator
          ? ["Select State","Select ZIP","Select School","Organization Info"][sub-1]
          : "Organization Info"}
      </SectionHeader>

      <Table>
        <tbody>
          {/* ─ Personal Info ─ */}
          <tr>
            <th colSpan="2"><h2>Personal Info</h2></th>
          </tr>
          <tr>
            <td colSpan="2">
              {data.title} {data.first_name} {data.last_name}<br/>
              {data.street}<br/>
              {data.street2 && <> {data.street2}<br/></>}
              {data.city}, {data.state} {data.zip}<br/>
              {data.phone}{data.ext && ` ext. ${data.ext}`}<br/>
              {data.fax && `fax: ${data.fax}`}<br/>
              {data.email}
            </td>
          </tr>

          {/* ─ Membership Type ─ */}
          <tr>
            <th><h4>Membership Type</h4></th>
            <td>{MEMBERSHIP_LABELS[data.membership] || data.membership}</td>
          </tr>

          {isEducator ? (
            <>
              {/* ─ Educator: State ─ */}
              {sub === 1 && (
                <tr>
                  <th><h4><label htmlFor="org_state">State*</label></h4></th>
                  <td>
                    <Select
                      id="org_state"
                      name="org_state"
                      value={data.org_state || ""}
                      onChange={onChange}
                    >
                      <option value="">Select a state</option>
                      {Object.entries(STATES).map(([name, code]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </Select>
                  </td>
                </tr>
              )}

              {/* ─ Educator: ZIP ─ */}
              {sub === 2 && (
                <tr>
                  <th><h4><label htmlFor="org_zip">ZIP*</label></h4></th>
                  <td>
                    <Select
                      id="org_zip"
                      name="org_zip"
                      value={data.org_zip||""}
                      onChange={onChange}
                    >
                      <option value="">Select ZIP</option>
                      {zipcodes?.map(z => (
                        <option key={z} value={z}>{z}</option>
                      ))}
                    </Select>
                  </td>
                </tr>
              )}

              {/* ─ Educator: School ─ */}
              {sub === 3 && (
                <tr>
                  <th><h4><label htmlFor="org_id">School or District*</label></h4></th>
                  <td>
                    <Select
                      id="org_id"
                      name="org_id"
                      value={data.org_id||""}
                      onChange={e => {
                        // 1) set org_id
                        onChange(e);
                        // 2) auto-fill org_name
                        const sel = schools?.find(s => s.org_id === e.target.value);
                        onChange({
                          target: { name: "org_name", value: sel ? sel.org_name : "" }
                        });
                      }}
                    >
                      <option value="">Select School</option>
                      {schools?.map(s => (
                        <option key={s.org_id} value={s.org_id}>
                          {s.org_name}
                        </option>
                      ))}
                    </Select>
                    <br/>
                    <small>If not listed, enter it below:</small><br/>
                    <Input
                      type="text"
                      name="org_name"
                      placeholder="Custom school name"
                      value={data.org_name||""}
                      onChange={onChange}
                    />
                  </td>
                </tr>
              )}

              {/* ─ Educator: Final ─ */}
              {sub === 4 && (
                <>
                  <tr>
                    <th colSpan="2"><h2>Organization Info</h2></th>
                  </tr>
                  <tr>
                    <th><h4>School or Organization</h4></th>
                    <td>{data.org_name}</td>
                  </tr>
                  <tr>
                    <th><h4>i-Parent Board Member</h4></th>
                    <td>
                      <input
                        name="iparent_board"
                        type="checkbox"
                        checked={!!data.iparent_board}
                        onChange={onChange}
                      />{" "}
                      Parents and parent advocates that desire to adopt ISAFE…
                    </td>
                  </tr>
                  <tr>
                    <th><h4>Newsletter</h4></th>
                    <td>
                      <input
                        name="news_letter"
                        type="checkbox"
                        checked={data.news_letter}
                        onChange={onChange}
                      />{" "}
                      Sign up for the ISAFE Newsletters…
                    </td>
                  </tr>
                </>
              )}
            </>
          ) : (
            <>
              {/* ─ Non-educator: Org Info ─ */}
              <tr>
                <th colSpan="2"><h2>Organization Info</h2></th>
              </tr>
              <tr>
                <td>
                  <label htmlFor="field-org_name">School or Organization</label>
                </td>
                <td>
                  <Input
                    id="field-org_name"
                    name="org_name"
                    value={data.org_name}
                    onChange={onChange}
                    size="40"
                  />
                </td>
              </tr>
              <tr>
                <td><label htmlFor="field-school_level">School Level</label></td>
                <td>
                  <Select
                    id="field-school_level"
                    name="school_level"
                    value={data.school_level||""}
                    onChange={onChange}
                  >
                    <option value=""></option>
                    {schoolLevels?.map(sl => (
                      <option key={sl.school_level} value={sl.school_level}>
                        {sl.school_level_desc.trim()}
                      </option>
                    ))}
                  </Select>{" "}
                  <span>if applicable</span>
                </td>
              </tr>
              <tr>
                <td><label htmlFor="field-iparent_board">i-Parent Board Member</label></td>
                <td>
                  <input
                    id="field-iparent_board"
                    name="iparent_board"
                    type="checkbox"
                    checked={!!data.iparent_board}
                    onChange={onChange}
                  />{" "}
                   Parents and parent advocates that desire to adopt ISAFE and Internet safety education as a special project for their schools and communities.
                </td>
              </tr>
              <tr>
                <td colSpan="2"><h2>Newsletter</h2></td>
              </tr>
              <tr>
                <td colSpan="2">
                  <input
                    id="field-news_letter"
                    name="news_letter"
                    type="checkbox"
                    checked={data.news_letter}
                    onChange={onChange}
                  />{" "}
                   Sign up for the ISAFE Newsletters and receive email updates.
                </td>
              </tr>
            </>
          )}
        </tbody>
      </Table>
    </Section>
  );
}
